import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { spineLoader } from '@play-co/timestep-spine';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { now, user } from 'src/StateObserver';
import {
  cardsGetMissingCardIds,
  cardsIsLocked,
  cardsShouldShowHudButton,
} from 'src/replicant/cards/cards.getters';
import { CARDS_REWARD_ICON_PATH } from '../cards.assets';
import { cardsHasNewStuff } from '../cards.sequence';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import { cardsCatalog } from 'src/replicant/cards/cards.ruleset';
import sounds from 'src/lib/sounds';

/** Number of unique cards in the active Cards album. */
const TOTAL_CARDS = Object.keys(cardsCatalog).length;

/** Assets used by the animated Cards HUD button. */
const assets = {
  cardIcon: CARDS_REWARD_ICON_PATH,
  progressBase: 'assets/ui/cards/hudButton/progress_base.png',
  progressFill: 'assets/ui/cards/hudButton/progress_fill.png',
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/cards/hudButton/spine/spine.cardbook_HUDicon',
  },
};

export class CardsButton extends FeatureButton {
  private progressBar!: ProgressBar;

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      lockedOverlay: {},
      labelPanel: {
        font: 'Title',
        offsetX: 0,
        offsetY: 1,
      },
      onDown: () => sounds.playSound('ok'),
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.spine.url,
        scale: DOWNSCALE_FACTOR,
        sequence: ['icon_idle'],
        onEnter: () => this.update(),
      },
    };

    this.progressBar = new ProgressBar({
      superview: this.container,
      bgStyle: {
        image: assets.progressBase,
        width: downscale(195),
        height: downscale(64),
        centerOnOrigin: true,
        x: this.style.width * 0.5,
        y: this.style.height * 0.5 + 65,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 60, right: 60 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
      },
      fillStyle: {
        image: '',
        width: downscale(171),
        height: downscale(42),
        scaleMethod: 'stretch',
        clip: true,
        x: 8,
        y: 3,
        paddingRight: 14,
      },
      domain: [0, TOTAL_CARDS],
      progress: 0,
      minWidth: 0,
    });

    // Clip the reusable fill image inside the current progress width.
    new ImageScaleView({
      superview: this.progressBar.progressFill,
      image: assets.progressFill,
      width: this.progressBar.progressFill.style.width,
      height: this.progressBar.progressFill.style.height,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
        vertical: { top: 0, bottom: 0 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });

    this.setNotification({
      condition: (state, timestamp) =>
        !cardsIsLocked(state, timestamp) && cardsHasNewStuff(),
      positioning: { align: 'right' },
    });

    this.update();
  }

  public override getElementsToApplyFilter(): View[] {
    const elements: View[] = [];
    if (this.spine) elements.push(this.spine);
    if (this.progressBar) elements.push(this.progressBar);
    if (this.labelPanel) elements.push(this.labelPanel);
    return elements;
  }

  public update() {
    const timestamp = now();

    // Hide the entry point when the existing Cards visibility rules reject it.
    if (!cardsShouldShowHudButton(user(), timestamp)) {
      this.hide();
      return;
    }

    // Show the animated button and keep its idle animation running.
    this.show();
    void this.setAnimationState('main');

    // Show unique cards collected against the active album total.
    const collectedCards =
      TOTAL_CARDS - cardsGetMissingCardIds(user(), timestamp).length;
    this.progressBar.progress = collectedCards;
    this.setLabelText(`${collectedCards}/${TOTAL_CARDS}`);

    // Preserve the existing locked preview behavior.
    if (cardsIsLocked(user(), timestamp)) {
      this.lock();
      this.updateOpts({ blockEvents: false });
      return;
    }

    // Unlock available players.
    if (this.isLocked) {
      void this.unlock(true);
    }
  }

  /** Play the Cards collection animation when a card reaches the HUD button. */
  public async playHitAnimation() {
    // The asset may still be loading when an early reward animation completes.
    if (!this.spine) return;

    // Play the collection reaction and return to the looping idle state.
    await playSpineAnimationSequence(this.spine, {
      sequence: ['icon_collect', 'icon_collect_to_idle', 'icon_idle'],
      loopLast: true,
      clear: true,
    });
  }
}
